/**
 * Fired on `window` right after a batch payment or individual scheduled payment submission comes
 * back requiring multi-sign approval - lets the nav's approval-required badge
 * (useApprovalRequiredCount) refresh immediately instead of waiting for its next poll tick.
 *
 * A full refetch (fetchPaymentsByGroup - 3 API calls) rather than a local count change, because
 * there's no cheap way to know from here whether the new submission is genuinely a net-new item
 * or something the badge's own poll already picked up moments earlier - the refetch is the source
 * of truth. Contrast with dispatchApprovalRequiredResolved below, which knows its delta exactly
 * and skips the refetch entirely.
 *
 * Follows the same plain-Event, dispatch-at-the-call-site / listen-in-a-hook idiom as
 * 'auth:refresh-failed' (see BaseApi.ts / AuthProvider.tsx) - the only other custom window event
 * in this codebase.
 */
export const PAYMENTS_APPROVAL_REQUIRED_EVENT = 'payments:approval-required-changed';

export const dispatchApprovalRequiredChanged = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new Event(PAYMENTS_APPROVAL_REQUIRED_EVENT));
};

/**
 * Fired on `window` right after the *current user* fully resolves a payment out of "needs
 * approval" - either their own approval was the last one required (the action reached
 * MultiSignActionStateEnum.COMPLETED), or they rejected it outright. Either way, this one item
 * has definitely left the pending-approval count, so the badge (useApprovalRequiredCount) can
 * just decrement its local count by one instead of paying for a full fetchPaymentsByGroup
 * refetch, the way dispatchApprovalRequiredChanged above does.
 *
 * Deliberately NOT fired for a non-final approval (MultiSignActionStateEnum.PENDING - more
 * approvers still required): the item stays counted either way, so there's nothing to decrement,
 * and firing this would incorrectly shrink the badge by one until the next full refresh corrects
 * it. See the call sites in useScheduledPaymentAction's approve()/confirmReject().
 */
export const PAYMENTS_APPROVAL_REQUIRED_RESOLVED_EVENT = 'payments:approval-required-resolved';

export const dispatchApprovalRequiredResolved = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new Event(PAYMENTS_APPROVAL_REQUIRED_RESOLVED_EVENT));
};
